import { useEffect, useMemo, useRef, useState } from 'react';

import {
  createU21FilterOptions,
  processPastedText,
} from 'app/shared/u21-ui/components/input/select/utils';
import { debounce } from 'lodash';

import {
  U21MultiSelect,
  U21MultiSelectProps,
} from 'app/shared/u21-ui/components/input/select/U21MultiSelect';
import {
  U21Select,
  U21SelectOptionProps,
  U21SelectProps,
  U21SelectValue,
} from 'app/shared/u21-ui/components/input/select/U21Select';

interface U21SearchPropsBase {
  delay?: number;
  onSearch: (value?: string) => void;
  onExactSearch?: (value: string[]) => void;
}

export interface U21SelectSearchSingleProps<
  TValue extends U21SelectValue,
  TClearable extends boolean = true,
>
  extends Omit<U21SelectProps<TValue, TClearable>, 'ref'>, U21SearchPropsBase {
  multi?: false;
}

export interface U21SelectSearchMultiProps<
  TValue extends U21SelectValue,
  TClearable extends boolean = true,
>
  extends
    Omit<U21MultiSelectProps<TValue, TClearable>, 'ref'>,
    U21SearchPropsBase {
  multi: true;
}

export type U21SelectSearchProps<
  TValue extends U21SelectValue,
  TClearable extends boolean = true,
> =
  | U21SelectSearchSingleProps<TValue, TClearable>
  | U21SelectSearchMultiProps<TValue, TClearable>;

export const U21SelectSearch = <
  TValue extends U21SelectValue,
  TClearable extends boolean = true,
>(
  props: U21SelectSearchProps<TValue, TClearable>,
) => {
  const {
    clearable,
    delay = 300,
    inputProps,
    loading,
    multi,
    onChange,
    onExactSearch,
    onSearch,
    options,
    value,
    ...rest
  } = props;
  const [searchValue, setSearchValue] = useState<string>('');
  const valueText = useMemo(() => {
    const valueOption = options.find((i) => {
      if (i.optionType === undefined) {
        return i.value === value;
      }
      return false;
    });
    if (valueOption) {
      // safe to cast here bc if valueOption is truthy from above it must be of type U21SelectOptionProps
      return String((valueOption as U21SelectOptionProps<TValue>).text);
    }
    return '';
  }, [options, value]);

  // used instead of loading due to debounce
  const [internalLoading, setInternalLoading] = useState(loading);

  const onSearchDebounced = useMemo(
    () => debounce(onSearch, delay),
    [delay, onSearch],
  );
  const onSearchDebouncedRef = useRef(onSearchDebounced);
  onSearchDebouncedRef.current = onSearchDebounced;

  useEffect(() => {
    onSearchDebouncedRef.current(undefined);
  }, []);

  useEffect(() => {
    if (!loading) {
      setInternalLoading(false);
    }
  }, [loading, options]);

  const commonProps:
    | Omit<U21SelectProps<TValue, TClearable>, 'onChange' | 'options' | 'value'>
    | Omit<
        U21MultiSelectProps<TValue, TClearable>,
        'onChange' | 'options' | 'value'
      > = {
    filterOptions: (selectOptions, state) => {
      if (!searchValue || internalLoading) {
        return selectOptions;
      }
      return createU21FilterOptions<TValue>(selectOptions, state);
    },
    inputProps: {
      delay: 0,
      ...inputProps,
      onKeyDown: (e) => {
        inputProps?.onKeyDown?.(e);
        const { key } = e;
        // disable removing selected behavior
        if (key === 'Backspace' || key === 'Delete') {
          e.stopPropagation();
        }
        if (key === 'Enter') {
          onSearchDebounced(searchValue);
        }
      },
    },
    loading: internalLoading,
    onInputChange: (e, newSearchValue, reason) => {
      if (reason === 'input') {
        setSearchValue(newSearchValue);
        if (newSearchValue) {
          onSearchDebounced(newSearchValue);
          setInternalLoading(true);
        }
      } else if (reason === 'clear') {
        setSearchValue('');
      }
    },
  };

  if (multi) {
    return (
      <U21MultiSelectSearch
        {...rest}
        {...commonProps}
        clearable={clearable}
        inputValue={searchValue}
        // for some reason generics aren't working in this component so manually define TValue
        onChange={(newValue: TValue[], e) => {
          setSearchValue('');
          onChange(newValue, e);
        }}
        onExactSearch={onExactSearch}
        onSearch={onSearch}
        options={options}
        setSearchValue={setSearchValue}
        searchValue={searchValue}
        value={value}
      />
    );
  }
  return (
    <U21Select
      allowInvalidValues
      {...rest}
      {...commonProps}
      clearable={clearable}
      // searchValue takes precedence over valueText since it will contain it
      inputValue={searchValue || valueText}
      onChange={(
        // for some reason generics aren't working in this component so manually define TValue
        newValue: TClearable extends true ? TValue | undefined : TValue,
        e,
      ) => {
        setSearchValue('');
        onChange(newValue, e);
      }}
      options={options}
      value={value}
    />
  );
};

const DEFAULT_VALUE = [];

const U21MultiSelectSearch = <
  TValue extends U21SelectValue,
  TClearable extends boolean = true,
>(
  props: U21MultiSelectProps<TValue, TClearable> & {
    onExactSearch?: U21SearchPropsBase['onExactSearch'];
    onSearch: U21SearchPropsBase['onSearch'];
    setSearchValue: (value: string) => void;
    searchValue: string;
  },
) => {
  const { onExactSearch, onSearch, setSearchValue, searchValue, ...rest } =
    props;
  const { onChange, options, value = DEFAULT_VALUE } = rest;

  const pastedValue = useRef('');

  // use refs to avoid trigger useEffect when these values change
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  const setSearchValueRef = useRef(setSearchValue);
  setSearchValueRef.current = setSearchValue;

  // autoselect options if it matches pastedValue when options are updated
  useEffect(() => {
    const { newOptions, newValues, remainingText } = processPastedText(
      pastedValue.current,
      options,
      valueRef.current,
    );

    // ignore if there are no exact matches
    if (!newOptions.length) {
      return;
    }

    onChangeRef.current([...valueRef.current, ...newValues]);
    setSearchValueRef.current(remainingText);

    if (remainingText) {
      onSearchRef.current(remainingText);
    }

    // known flaw here where we could get the option for the remaining text
    // after the user cleared the search input already
    pastedValue.current = remainingText;
  }, [options]);

  return (
    <U21MultiSelect
      onPaste={(e) => {
        const pastedData = e.clipboardData.getData('text');
        const {
          hasValidSeparators,
          newOptions,
          newValues,
          remainingText,
          searchTerms,
        } = processPastedText(pastedData, options, value);

        pastedValue.current = remainingText;

        const canExactSearch = Boolean(onExactSearch && searchTerms.length);

        if (hasValidSeparators && !newOptions.length && !canExactSearch) {
          return;
        }

        e.preventDefault();

        // Select any options that already matched the pasted text exactly.
        if (newOptions.length) {
          onChange([...value, ...newValues]);
        }

        if (!hasValidSeparators) {
          if (pastedData) {
            onExactSearch?.([pastedData]);
            setSearchValue(pastedData);
            onSearch(pastedData);
          }
        } else if (canExactSearch) {
          onExactSearch?.(searchTerms);
          if (remainingText) {
            setSearchValue(remainingText);
          }
        } else if (remainingText) {
          setSearchValue(remainingText);
          onSearch(remainingText);
        }
      }}
      {...rest}
    />
  );
};
